/**
 * Auth API – mirrors POST /api/v1/login, POST /api/v1/users (register),
 * and POST /api/v1/token/refresh in vin-pulse-os-api.
 *
 * The backend's /login response is the canonical contract:
 *   { accessToken, refreshToken, user: { id, email, full_name, role } }
 *
 * For ecommerce we register customers as users with role='Customer' via
 * POST /api/v1/users. That endpoint enforces password length >= 12 chars,
 * which the AuthModal validation already accommodates by surfacing the
 * backend error verbatim.
 */

import axios from 'axios';
import { apiClient } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export interface BackendUser {
  id: number;
  email: string;
  full_name: string;
  role: string;
  // Forced-reset gate. The backend sets this to true for admin-onboarded
  // customers (created with a shared temporary password) and clears it once
  // POST /users/me/password succeeds. Both /login and /users/me return it.
  must_change_password?: boolean;
  // Staff profile fields (migration 027 on vin-pulse-os-api). Customer-
  // facing screens don't use most of these, but `phone` is mapped through
  // to the User type so any admin-entered value shows up on Profile.
  phone?: string | null;
  employee_id?: string | null;
  department?: string | null;
  territory?: string | null;
  warehouse_code?: string | null;
  notes?: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: BackendUser;
}

export interface RegisterUserInput {
  email: string;
  password: string;
  full_name: string;
  role?: string; // Defaults to 'Customer' for ecommerce signups
}

export async function loginRequest(email: string, password: string): Promise<LoginResponse> {
  // Login is unauthenticated – use bare axios so we don't accidentally pass
  // a stale Bearer header through the interceptor.
  const response = await axios.post<any>(`${API_BASE_URL}/login`, { email, password });
  const data = response.data?.data ?? response.data;
  if (!data?.accessToken || !data?.refreshToken || !data?.user) {
    throw new Error('Login failed – invalid response from server');
  }
  apiClient.setTokens(data.accessToken, data.refreshToken);
  return data as LoginResponse;
}

export async function registerRequest(input: RegisterUserInput): Promise<BackendUser> {
  // Public customer self-registration hits POST /api/v1/register, which
  // hard-locks role to 'Customer' (admin-only POST /users can't be called
  // unauthenticated). Like loginRequest, use bare axios so no stale Bearer
  // header is attached during this unauthenticated call.
  const body = { ...input, role: input.role ?? 'Customer' };
  const response = await axios.post<any>(`${API_BASE_URL}/register`, body);
  const data = response.data?.data ?? response.data;
  if (!data?.user) {
    throw new Error('Registration failed – invalid response from server');
  }
  return data.user as BackendUser;
}

export function logoutRequest(): void {
  apiClient.clearTokens();
}

/**
 * Self-service password change – mirrors POST /api/v1/users/me/password.
 * Verifies the current password and sets a new one (backend enforces a
 * 12-char minimum and rejects reusing the same password). On success the
 * backend also clears `must_change_password`. Requires a valid session, so
 * this runs through `apiClient` (Bearer token attached automatically).
 */
export async function changePasswordRequest(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await apiClient.post<void>('/users/me/password', { currentPassword, newPassword });
}

export async function refreshSession(): Promise<LoginResponse | null> {
  // Used on app boot to verify a stored token is still valid by hydrating
  // the user via /users/me. Returns null if the token is missing or invalid.
  if (!apiClient.isAuthenticated()) return null;
  try {
    const me = await apiClient.get<BackendUser>('/users/me');
    return {
      accessToken: apiClient.getAccessToken() ?? '',
      refreshToken: '',
      user: me,
    };
  } catch {
    apiClient.clearTokens();
    return null;
  }
}
